import {
  noop,
  queryOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import React from "react";
import type { LocString } from "@/shared/schema/schema.ts";
import { http, queryClient } from "@/shared/util/fetch.tsx";

export interface SkillProficiencyLabels {
  name?: string;
  description?: string;
}

export type ProficiencyLevel = 1 | 2 | 3 | 4 | 5;
export type ProficiencyFilterLevel = 0 | ProficiencyLevel;

// this list is not exhaustive, but it's a good start
export interface Settings {
  allow_external_proposal_sharing?: boolean;
  allow_proposal_specific_api_keys?: boolean;
  enable_skill_proficiency?: boolean;
  international_enabled?: boolean;
  require_upn?: boolean;
  sensitive_fields_enabled?: boolean;
  show_add_skill_outside_project_experience?: boolean;
  show_certifications?: boolean;
  show_courses?: boolean;
  show_key_qualifications_key_points?: boolean;
  show_project_experiences_area?: boolean;
  show_project_experiences_customer_contacts?: boolean;
  show_project_experiences_customer_description?: boolean;
  show_project_experiences_customer_value_proposition?: boolean;
  show_project_experiences_extent_hours?: boolean;
  show_project_experiences_location_country_code?: boolean;
  show_project_experiences_percent_allocated?: boolean;
  show_project_experiences_project_extent?: boolean;
  show_project_experiences_project_extent_hours?: boolean;
  show_project_experiences_project_type?: boolean;
  show_project_experiences_related_work_experience_id?: boolean;
  show_project_experiences_roles_summary?: boolean;
  show_project_experiences_total_extent?: boolean;
  show_project_experiences_total_extent_hours?: boolean;
  show_project_experiences_location_delivery?: boolean;
  show_project_experiences_contract_type?: boolean;
  show_project_experiences_images?: boolean;
  show_references_tab?: boolean;
  limit_reference_draft_creation?: boolean;

  educations?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  certifications?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  courses?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  project_experiences?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  blogs?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  positions?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  mentorings?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  honors_awards?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  presentations?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  recommendations?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  work_experiences?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  personal_details?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  key_qualifications?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  languages?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  cv_roles?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  technologies?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  highlighted_roles?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  publications?: {
    disable_ui_edits_for_section_types?: boolean;
  };
  project_reference?: {
    disable_ui_edits_for_section_types?: boolean;
  };

  // string or number?
  project_experiences_long_description_recommended_length?: number;
  // string or number?
  project_experiences_roles_long_description_recommended_length?: number;

  skill_proficiency_labels?: Record<
    string,
    Partial<Record<ProficiencyLevel, SkillProficiencyLabels>> | undefined
  >;
  skill_proficiency_levels?: 3 | 5;

  external_unique_id_help_texts?: LocString;
  upn_help_texts?: LocString;

  show_blogs?: boolean;
  show_positions?: boolean;
  show_mentorings?: boolean;
  show_honors_awards?: boolean;
  show_presentations?: boolean;
  show_recommendations?: boolean;
  show_work_experiences?: boolean;
  show_reference_project_area?: boolean;
  show_reference_project_type?: boolean;
  show_reference_project_contract_type?: boolean;
  show_personal_details_twitter?: boolean;
  show_reference_project_extent?: boolean;
  show_personal_details_landline?: boolean;
  show_personal_details_born_year?: boolean;
  show_personal_details_external_unique_id?: boolean;
  show_personal_details_place_of_residence?: boolean;
  show_key_qualifications_tag_line?: boolean;
  show_reference_project_architect?: boolean;
  show_personal_details_nationality?: boolean;
  show_reference_project_extent_old?: boolean;
  show_reference_project_extent_hours?: boolean;
  show_reference_project_introduction?: boolean;
  show_reference_project_total_extent?: boolean;
  show_personal_details_name_multilang?: boolean;
  show_reference_project_building_owner?: boolean;
  show_reference_project_extent_hours_old?: boolean;
  show_reference_project_total_extent_hours?: boolean;
  show_reference_project_location_country_code?: boolean;
  show_reference_project_collaborating_partners?: boolean;
  key_qualifications_tag_line_recommended_length?: string;
  show_key_qualifications_key_points_long_description?: boolean;
  // string or number?
  key_qualifications_long_description_recommended_length?: string;
}

export type SettingsKeys = keyof Settings;

interface LastModified {
  id: string;
  name: string | null;
  updated_at: string;
}

type LastModifiedSettings = {
  [key in SettingsKeys]: LastModified;
};

const baseKey = "interface-setting";

const queries = {
  settings: queryOptions({
    queryKey: [baseKey, "settings"] as const,
    queryFn: ({ signal }) =>
      // the only remaining interface setting exposed externally
      http.get<Settings>("/api/v1/interface_setting", { signal }),
  }),
  lastModified: queryOptions({
    queryKey: [baseKey, "lastModified"] as const,
    queryFn: ({ signal }) =>
      http.get<LastModifiedSettings>("/interface_setting/last_modified", {
        signal,
      }),
  }),
};

const InterfaceSettingsContext = React.createContext<
  Settings | undefined | null
>(null);

export const InterfaceSettingsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { data } = useQuery({ ...queries.settings, staleTime: 60_000 });

  return (
    <InterfaceSettingsContext value={data}>{children}</InterfaceSettingsContext>
  );
};

export function useInterfaceSettings() {
  const settingsContext = React.use(InterfaceSettingsContext);

  // If `context` is `null` (not `undefined`, as that means "loading"), we
  // fetch the data here. This is to guard against missing ContextProvider.
  // This avoids setting up hundreds of extra `QueryObserver`s.
  const { data } = useQuery({
    ...queries.settings,
    staleTime: 60_000,
    enabled: settingsContext === null,
  });

  return settingsContext ?? data;
}

export function useInterfaceSetting<K extends SettingsKeys>(
  key: K,
): Settings[K] | undefined {
  const data = useInterfaceSettings();

  if (!data) {
    return;
  }

  return data[key] ?? undefined;
}

export function useInterfaceSettingLastModified<K extends SettingsKeys>(
  key: K,
): LastModifiedSettings[K] | undefined {
  const { data } = useQuery({ ...queries.lastModified, staleTime: 60_000 });

  if (!data) {
    return;
  }

  return data[key] ?? undefined;
}

export function useUpdateInterfaceSetting() {
  const { mutate } = useMutation({
    mutationKey: ["interface-setting-update"],
    mutationFn: (interface_setting: Settings) =>
      http.put<Settings>("/interface_setting", { body: { interface_setting } }),
    onMutate: (settings, { client }) => {
      client.setQueryData(queries.settings.queryKey, (existingData) => {
        if (!existingData) {
          return existingData;
        }

        return { ...existingData, ...settings };
      });
    },
    onSuccess: (data, _vars, _onMutateResult, { client }) =>
      client.setQueryData(queries.settings.queryKey, data),
    onError: (_error, _vars, _onMutateResult, { client }) =>
      client.invalidateQueries(queries.settings),
    onSettled: (_data, _error, _vars, _onMutateResult, { client }) =>
      client.invalidateQueries(queries.lastModified),
  });

  return mutate;
}

export async function prefetchInterfaceSettings() {
  await queryClient
    .query({ ...queries.settings, staleTime: Infinity })
    // eslint-disable-next-line unicorn/prefer-await
    .catch(noop);
}

export async function prefetchInterfaceSettingsLastModified() {
  await queryClient
    .query({
      ...queries.lastModified,
      staleTime: Infinity,
    })
    // eslint-disable-next-line unicorn/prefer-await
    .catch(noop);
}

export function getLengthValue(
  settings: Settings,
  key: SettingsKeys | undefined,
): number | undefined {
  if (!key) {
    return undefined;
  }
  const length = settings[key];

  if (typeof length === "number") {
    return Math.floor(length);
  }

  if (typeof length === "string") {
    const trimmed = length.trim();
    if (trimmed === "") {
      return undefined;
    }
    if (/^\d+(\.\d+)?$/.test(trimmed)) {
      return Math.floor(Number(trimmed));
    }
  }

  return undefined;
}
